// Validation helpers for runtime manifest JSON payloads.
// They keep schema checks and warning creation isolated from fetch logic.
import type { LuisCompileWarning } from '@lentystyle/compiler'
import type { ManifestCacheEntry, LuisManifestDocument, LuisManifestRule } from './manifest-types'

// Validate manifest JSON shape and collect non-fatal rule warnings.
export function validateManifestDocument(payload: unknown, manifestUrl: string): ManifestCacheEntry {
  // Reject non-object manifest payload values.
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {
      document: null,
      warnings: [buildManifestWarning(`[${manifestUrl}] Manifest root must be an object.`)],
    }
  } else {
    // Continue object-shape validation.
  }

  const version = (payload as { version?: unknown }).version
  // Reject unsupported manifest versions.
  if (version !== 1) {
    return {
      document: null,
      warnings: [buildManifestWarning(`[${manifestUrl}] Unsupported manifest version. Expected: 1.`)],
    }
  } else {
    // Continue rules-array validation.
  }

  const rawRules = (payload as { rules?: unknown }).rules
  // Reject non-array rules payloads.
  if (!Array.isArray(rawRules)) {
    return {
      document: null,
      warnings: [buildManifestWarning(`[${manifestUrl}] Manifest rules must be an array.`)],
    }
  } else {
    // Continue per-rule validation for array payload.
  }

  const rules: LuisManifestRule[] = []
  const warnings: LuisCompileWarning[] = []
  // Validate each rule and keep only supported items.
  for (let index = 0; index < rawRules.length; index += 1) {
    const rule = rawRules[index]
    const validated = validateManifestRule(rule)
    if (!validated) {
      warnings.push(buildManifestWarning(`[${manifestUrl}] Ignored invalid rule at index ${index}.`))
      continue
    } else {
      // Continue with valid rule entry.
    }

    rules.push(validated)
  }

  // Return validated manifest document with collected warnings.
  return {
    document: { version: 1, rules },
    warnings,
  }
}

// Build mode-override resolver from validated suffix rules.
export function buildManifestModeResolver(rules: LuisManifestRule[]): (sourceId: string) => 'full' | 'vendor-fast' | undefined {
  // Return first matching suffix rule mode for current source id.
  return (sourceId: string) => {
    const normalizedSourceId = normalizeSourceIdForSuffixMatch(sourceId)
    for (const rule of rules) {
      if (rule.matchType !== 'suffix') {
        continue
      } else if (!normalizedSourceId.endsWith(rule.value)) {
        continue
      } else if (!rule.mode) {
        continue
      } else {
        return rule.mode
      }
    }

    // Return undefined when no suffix rule matches source id.
    return undefined
  }
}

// Build execution-override resolver from validated suffix rules.
export function buildManifestExecutionResolver(rules: LuisManifestRule[]): (sourceId: string) => 'auto' | 'main' | 'worker' | undefined {
  // Return first matching suffix rule execution for current source id.
  return (sourceId: string) => {
    const normalizedSourceId = normalizeSourceIdForSuffixMatch(sourceId)
    for (const rule of rules) {
      if (rule.matchType !== 'suffix') {
        continue
      } else if (!normalizedSourceId.endsWith(rule.value)) {
        continue
      } else if (!rule.execution) {
        continue
      } else {
        return rule.execution
      }
    }

    // Return undefined when no suffix rule matches source id.
    return undefined
  }
}

// Validate one manifest rule entry.
function validateManifestRule(payload: unknown): LuisManifestRule | null {
  // Reject non-object rule payload values.
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null
  } else {
    // Continue object rule validation.
  }

  const matchType = (payload as { matchType?: unknown }).matchType
  const value = (payload as { value?: unknown }).value
  const mode = (payload as { mode?: unknown }).mode
  const execution = (payload as { execution?: unknown }).execution
  const hasValidMode = mode === undefined || mode === 'full' || mode === 'vendor-fast'
  const hasValidExecution = execution === undefined || execution === 'auto' || execution === 'main' || execution === 'worker'
  // Reject unsupported match types.
  if (matchType !== 'suffix') {
    return null
  } else if (typeof value !== 'string' || value.trim().length === 0) {
    return null
  } else if (!hasValidMode || !hasValidExecution) {
    return null
  } else if (mode === undefined && execution === undefined) {
    // Reject no-op rules without mode or execution.
    return null
  } else {
    // Continue normalized rule return.
  }

  // Return normalized suffix rule payload.
  return {
    matchType: 'suffix',
    value: value.trim(),
    mode,
    execution,
  }
}

// Remove query/hash suffixes before suffix-rule source matching.
function normalizeSourceIdForSuffixMatch(sourceId: string): string {
  const queryIndex = sourceId.indexOf('?')
  const hashIndex = sourceId.indexOf('#')
  const cutIndex = pickMinPositive(queryIndex, hashIndex)
  // Return source id unchanged when no query/hash exists.
  if (cutIndex === -1) {
    return sourceId
  } else {
    // Return source id without query/hash suffix.
    return sourceId.slice(0, cutIndex)
  }
}

// Pick smallest non-negative index from query/hash positions.
function pickMinPositive(left: number, right: number): number {
  if (left === -1) {
    return right
  } else if (right === -1) {
    return left
  } else {
    return Math.min(left, right)
  }
}

// Build one manifest warning payload in compiler-compatible format.
export function buildManifestWarning(message: string): LuisCompileWarning {
  // Return warning payload with stable manifest-warning code.
  return {
    code: 'invalid-manifest',
    line: 1,
    message,
  }
}
